import { useState } from "react";
import moment from "moment";
import { SurveyApi } from "@/apis/v2/SurveyApi";
import { customToast } from "@/components/toast/custom-react-toastify/v1";
import { useDeleteSurveyResponseMutation, useSurveyResponsesQuery, useSurveySummaryQuery } from "@/hooks/ReactQuery/SurveyReactQuery/SurveyReactQuery";
import { CHOICE_TYPES, GRID_TYPES, isAnswerable, OTHER_ID, typeLabel } from "../surveyQuestions";

const SEGMENT = (active) => `rounded-lg px-3 py-1.5 text-[13px] font-semibold transition ${active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`;

/** 답을 읽을 수 있는 글로 (지금 없는 선택지는 "(지운 선택지)", 기타는 "기타: 쓴 글", 그리드는 행마다 한 줄) */
function answerText(question, answer, otherText) {
    if (answer === undefined || answer === null) {
        return null;
    }
    const label = (id) => (id === OTHER_ID ? `기타: ${otherText || ''}` : question.options?.find(o => o.id === id)?.label ?? '(지운 선택지)');
    const labels = (value) => (Array.isArray(value) ? value.map(label).join(', ') : label(value));
    if (GRID_TYPES.includes(question.type)) {
        return question.rows.filter(row => answer[row.id] !== undefined).map(row => `${row.label}: ${labels(answer[row.id])}`).join('\n') || null;
    }
    if (Array.isArray(answer)) {
        return labels(answer);
    }
    if (typeof answer === 'object') {
        return null; // 유형을 바꾸기 전의 답 (그리드 등)
    }
    return CHOICE_TYPES.includes(question.type) ? label(answer) : String(answer);
}

/** 응답 탭: 요약(질문별 막대) · 개별 응답 · CSV 내려받기 */
export function FgSurveyResults({ survey }) {
    const [mode, setMode] = useState('SUMMARY');
    const summaryQuery = useSurveySummaryQuery({ id: survey.id });
    const total = summaryQuery.data?.total ?? survey.responseCount;

    const downloadCsv = async () => {
        try {
            const response = await SurveyApi.exportCsv({ id: survey.id });
            const href = URL.createObjectURL(response.data);
            const a = document.createElement('a');
            a.href = href;
            a.download = `${survey.title.replace(/[\\/:*?"<>|]/g, '_')}_응답_${moment().format('YYYYMMDD')}.csv`;
            a.click();
            URL.revokeObjectURL(href);
        } catch (error) {
            customToast.error('내려받지 못했어요. 다시 시도해 주세요.');
        }
    }

    return (
        <div className="flex flex-col gap-3 bg-[#f7f7f9] px-3 py-4 min-[993px]:px-5 min-[993px]:py-5">
            <div className="flex flex-wrap items-center gap-2">
                <div className="flex rounded-xl bg-gray-200/60 p-1" role="tablist" aria-label="응답 보기">
                    <button type="button" role="tab" aria-selected={mode === 'SUMMARY'} onClick={() => setMode('SUMMARY')} className={SEGMENT(mode === 'SUMMARY')}>요약</button>
                    <button type="button" role="tab" aria-selected={mode === 'EACH'} onClick={() => setMode('EACH')} className={SEGMENT(mode === 'EACH')}>개별 응답</button>
                </div>
                <button type="button" onClick={downloadCsv} disabled={!total} className="ml-auto rounded-xl bg-white px-3.5 py-2 text-[13px] font-semibold text-gray-700 ring-1 ring-gray-200 transition hover:bg-gray-50 disabled:opacity-40">
                    ⬇ CSV 내려받기
                </button>
            </div>

            {summaryQuery.isLoading
                ? <div className="h-60 animate-pulse rounded-2xl bg-white" aria-busy="true" />
                : !total
                    ? <div className="flex flex-col items-center gap-2 rounded-2xl bg-white px-6 py-14 text-center ring-1 ring-gray-100">
                        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[var(--mainColor100)] text-xl" aria-hidden="true">📭</span>
                        <p className="mt-2 text-[15px] font-bold text-gray-900">아직 응답이 없어요</p>
                        <p className="text-[13px] text-gray-500 break-keep">{survey.status === 'OPEN' ? '위의 "링크 복사"나 "내 페이지에 넣기"로 설문을 알려 보세요.' : '"응답 받기 시작"을 눌러야 응답을 받을 수 있어요.'}</p>
                    </div>
                    : mode === 'SUMMARY'
                        ? <Summary survey={survey} summary={summaryQuery.data} />
                        : <EachResponses survey={survey} />
            }
        </div>
    );
}

function Summary({ survey, summary }) {
    return (
        <>
            <div className="rounded-2xl bg-white px-5 py-4 ring-1 ring-gray-100">
                <p className="text-[12px] font-semibold text-gray-500">전체 응답</p>
                <p className="mt-0.5 text-[28px] font-extrabold tracking-tight text-gray-900">{summary.total.toLocaleString()}<span className="ml-1 text-[15px] font-bold text-gray-400">개</span></p>
            </div>
            {survey.questions.filter(isAnswerable).map((question, index) => {
                const stat = summary.questions.find(s => s.id === question.id);
                return (
                    <section key={question.id} className="rounded-2xl bg-white p-5 ring-1 ring-gray-100">
                        <p className="text-[11px] font-bold text-gray-400">Q{index + 1} · {typeLabel(question.type)}</p>
                        <h3 className="mt-1 break-words text-[15px] font-bold text-gray-900">{question.title}</h3>
                        <p className="mt-0.5 text-[12px] text-gray-500">응답 {stat?.answered ?? 0}개</p>
                        <div className="mt-4">
                            {CHOICE_TYPES.includes(question.type) && <ChoiceBars question={question} stat={stat} total={summary.total} />}
                            {(question.type === 'LINEAR_SCALE' || question.type === 'RATING') && <ScaleBars question={question} stat={stat} />}
                            {GRID_TYPES.includes(question.type) && <GridTable question={question} stat={stat} />}
                            {['SHORT_TEXT', 'LONG_TEXT', 'DATE'].includes(question.type) && <Samples stat={stat} />}
                        </div>
                    </section>
                );
            })}
        </>
    );
}

/** 선택지별 가로 막대 (체크박스는 응답자 대비 비율이라 합이 100% 를 넘을 수 있다) */
function ChoiceBars({ question, stat, total }) {
    const counts = stat?.counts || {};
    const max = Math.max(1, ...Object.values(counts));
    const removed = Object.entries(counts).filter(([id]) => id !== OTHER_ID && !question.options.some(o => o.id === id)).reduce((s, [, n]) => s + n, 0);
    const rows = [
        ...question.options.map(o => ({ id: o.id, label: o.label, count: counts[o.id] || 0 })),
        ...(question.allowOther || counts[OTHER_ID] ? [{ id: OTHER_ID, label: '기타', count: counts[OTHER_ID] || 0 }] : []),
        ...(removed ? [{ id: '_removed', label: '(지운 선택지)', count: removed }] : []),
    ];

    return (
        <>
        <ul className="flex flex-col gap-2.5">
            {rows.map(row => {
                const percent = total ? Math.round((row.count / total) * 100) : 0;
                const top = row.count > 0 && row.count === max;
                return (
                    <li key={row.id}>
                        <div className="mb-1 flex items-baseline justify-between gap-3 text-[13px]">
                            <span className="min-w-0 break-words font-medium text-gray-800">{row.label}</span>
                            <span className="shrink-0 tabular-nums text-gray-500"><b className="text-gray-900">{row.count}</b> · {percent}%</span>
                        </div>
                        <div className="h-2.5 overflow-hidden rounded-full bg-gray-100">
                            <div className={`h-full rounded-full transition-[width] duration-700 ${top ? 'bg-gradient-to-r from-[#ee9ca7] to-[#8b7cf6]' : 'bg-gray-300'}`} style={{ width: `${(row.count / max) * 100}%` }} />
                        </div>
                    </li>
                );
            })}
        </ul>
        {stat?.otherSamples?.length > 0 &&
            <div className="mt-4">
                <p className="mb-1.5 text-[12px] font-bold text-gray-500">"기타"에 쓴 답</p>
                <Samples stat={{ samples: stat.otherSamples, answered: counts[OTHER_ID] || 0 }} />
            </div>
        }
        </>
    );
}

/** 그리드: 행 × 열 표, 칸 색이 진할수록 많이 고름 */
function GridTable({ question, stat }) {
    const grid = stat?.grid || {};
    const peak = Math.max(1, ...question.rows.flatMap(row => question.options.map(col => grid[row.id]?.[col.id] || 0)));
    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[320px] border-separate border-spacing-1 text-[13px]">
                <thead>
                    <tr>
                        <th className="w-1/3" />
                        {question.options.map(col => <th key={col.id} scope="col" className="px-1 pb-1 text-center text-[12px] font-semibold text-gray-500">{col.label}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {question.rows.map(row => (
                        <tr key={row.id}>
                            <th scope="row" className="break-words pr-2 text-left font-medium text-gray-800">{row.label}</th>
                            {question.options.map(col => {
                                const count = grid[row.id]?.[col.id] || 0;
                                const strength = count / peak;
                                return (
                                    <td key={col.id} className={`h-10 rounded-lg text-center font-bold tabular-nums ${strength > 0.55 ? 'text-white' : 'text-gray-700'}`} style={{ background: count ? `rgba(139, 124, 246, ${0.12 + strength * 0.78})` : '#f3f4f6' }}>
                                        {count}
                                    </td>
                                );
                            })}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/** 값별 세로 막대 + 평균 */
function ScaleBars({ question, stat }) {
    const { min, max, minLabel, maxLabel } = question.scale;
    const values = Array.from({ length: max - min + 1 }, (_, i) => min + i);
    const counts = stat?.counts || {};
    const peak = Math.max(1, ...values.map(v => counts[v] || 0));

    return (
        <div className="flex flex-col gap-3 min-[600px]:flex-row min-[600px]:items-end">
            <div className="shrink-0 rounded-xl bg-gray-50 px-4 py-3 min-[600px]:w-28">
                <p className="text-[11px] font-semibold text-gray-500">평균</p>
                <p className="text-[24px] font-extrabold text-gray-900">{question.type === 'RATING' && stat?.average != null && <span className="mr-0.5 text-amber-400">★</span>}{stat?.average ?? '-'}</p>
            </div>
            <div className="min-w-0 flex-1">
                <div className="flex h-28 items-end gap-1.5">
                    {values.map(v => (
                        <div key={v} className="flex h-full min-w-0 flex-1 flex-col items-center justify-end gap-1">
                            <span className="text-[11px] font-semibold tabular-nums text-gray-500">{counts[v] || 0}</span>
                            <div className={`w-full rounded-t-md transition-[height] duration-700 ${(counts[v] || 0) === peak && counts[v] ? 'bg-gradient-to-t from-[#ee9ca7] to-[#8b7cf6]' : 'bg-gray-200'}`} style={{ height: `${Math.max(4, ((counts[v] || 0) / peak) * 100)}%` }} />
                        </div>
                    ))}
                </div>
                <div className="mt-1 flex gap-1.5">
                    {values.map(v => <span key={v} className="flex-1 text-center text-[12px] font-bold text-gray-700">{v}</span>)}
                </div>
                {(minLabel || maxLabel) && <div className="mt-1 flex justify-between text-[11px] text-gray-400"><span>{minLabel}</span><span>{maxLabel}</span></div>}
            </div>
        </div>
    );
}

/** 글·날짜: 최근 답 (서버가 최근 50개까지) */
function Samples({ stat }) {
    const samples = stat?.samples || [];
    if (!samples.length) {
        return <p className="text-[13px] text-gray-400">아직 답이 없어요.</p>;
    }
    return (
        <ul className="flex max-h-72 flex-col gap-1.5 overflow-y-auto pr-1">
            {samples.map((sample, i) => <li key={i} className="whitespace-pre-line break-words rounded-xl bg-gray-50 px-3.5 py-2.5 text-[14px] text-gray-800">{sample}</li>)}
            {stat.answered > samples.length && <li className="px-1 pt-1 text-[12px] text-gray-400">최근 {samples.length}개만 보여요. 전부는 개별 응답이나 CSV 로 보세요.</li>}
        </ul>
    );
}

function EachResponses({ survey }) {
    const responsesQuery = useSurveyResponsesQuery({ id: survey.id });
    const deleteResponseMutation = useDeleteSurveyResponseMutation();
    const pages = responsesQuery.data?.pages || [];
    const items = pages.flatMap(p => p.items);
    const total = pages[0]?.total || 0;
    const questions = survey.questions.filter(isAnswerable);

    const handleDelete = (response) => {
        if (!window.confirm('이 응답을 삭제할까요? 되돌릴 수 없어요.')) {
            return;
        }
        deleteResponseMutation.mutate({ id: survey.id, responseId: response.id });
    }

    if (responsesQuery.isLoading) {
        return <div className="h-60 animate-pulse rounded-2xl bg-white" aria-busy="true" />;
    }

    return (
        <>
            {items.map((response, i) => (
                <article key={response.id} className="rounded-2xl bg-white p-5 ring-1 ring-gray-100">
                    <div className="flex items-center gap-2">
                        <span className="rounded-full bg-gray-900 px-2 py-0.5 text-[11px] font-bold text-white">#{total - i}</span>
                        <span className="text-[12px] text-gray-500">{moment(response.createdAt).format('YYYY.MM.DD HH:mm')}</span>
                        <button type="button" onClick={() => handleDelete(response)} className="ml-auto rounded-lg px-2 py-1 text-[12px] font-semibold text-gray-400 hover:bg-gray-100 hover:text-[var(--redColor1)]">삭제</button>
                    </div>
                    <dl className="mt-3 flex flex-col divide-y divide-gray-100">
                        {questions.map(question => {
                            const text = answerText(question, response.answers?.[question.id], response.others?.[question.id]);
                            return (
                                <div key={question.id} className="py-2.5">
                                    <dt className="text-[12px] font-semibold text-gray-500">{question.title}</dt>
                                    <dd className={`mt-0.5 whitespace-pre-line break-words text-[14px] ${text ? 'text-gray-900' : 'text-gray-300'}`}>{text ?? '답 없음'}</dd>
                                </div>
                            );
                        })}
                    </dl>
                </article>
            ))}
            {responsesQuery.hasNextPage &&
                <button type="button" disabled={responsesQuery.isFetchingNextPage} onClick={() => responsesQuery.fetchNextPage()} className="self-center rounded-full bg-white px-6 py-2 text-sm font-semibold ring-1 ring-gray-200 hover:bg-gray-50">
                    {responsesQuery.isFetchingNextPage ? '불러오는 중...' : '더 보기'}
                </button>
            }
        </>
    );
}
